import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/axios';
import { useSeccionPermisos } from '../hooks/useSeccionPermisos';
import { useCustomCaseStatuses } from '../hooks/useCustomCaseStatuses';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Briefcase, ArrowLeft, FileText, CheckCircle, Clock, User, AlertTriangle,
  RefreshCw, X, Plus, Eye, Edit2, Calendar, Activity, Shield, ChevronDown,
  LayoutGrid, List, Tag, Share2, Mail, ChevronLeft, ChevronRight, Zap,
  ShieldAlert, Headphones, Scale, StickyNote, Upload, ClipboardList,
  FileOutput, Trash2, History, Radar, Lock, XCircle, BarChart3, Loader2, Send, GitMerge, Building2, Search
} from 'lucide-react';
import ROSEvaluationPanel from '../components/ROSEvaluationPanel';
import InformesPanel from '../components/InformesPanel';
import AlertasEmbebidas from '../components/AlertasEmbebidas';
import CasoResumen from '../components/caso/CasoResumen';
import CasoTimeline from '../components/caso/CasoTimeline';
import CasoSolicitudes from '../components/caso/CasoSolicitudes';
import CasoInvestigar from '../components/caso/CasoInvestigar';
import { CaseLinksPanel } from '../components/CaseLinksPanel';
import PreviousCasesBanner from '../components/caso/PreviousCasesBanner';
import CasoComparativaTab from '../components/caso/CasoComparativaTab';
import EscalationBanner from '../components/caso/EscalationBanner';
import AlertMovimientosPreview from '../components/caso/AlertMovimientosPreview';
import TransactionCharts from '../components/caso/TransactionCharts';
import ClienteHistorial from '../components/caso/ClienteHistorial';
import InformeVinculacion from '../components/caso/InformeVinculacion';
import { CasoDetalleDialogs } from '../components/caso/CasoDetalleDialogs';
import { CasoMovimientosTab } from '../components/caso/CasoMovimientosTab';
import { CasoDocumentosTab } from '../components/caso/CasoDocumentosTab';
import { CasoCommentsSection, CasoInfoPanel } from '../components/caso/CasoBottomPanels';
import CasoExportPDF from '../components/caso/CasoExportPDF';

const ESTADOS = [
  { value: 'ABIERTO', label: 'Abierto', color: 'bg-blue-100 text-blue-800' },
  { value: 'PENDIENTE', label: 'Pendiente', color: 'bg-amber-100 text-amber-800' },
  { value: 'EN_INVESTIGACION', label: 'En Investigación', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'PENDIENTE_APROBACION', label: 'Pendiente Aprobación', color: 'bg-purple-100 text-purple-800' },
  { value: 'EN_REVISION', label: 'En Revisión', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'ESCALADO', label: 'Escalado', color: 'bg-rose-100 text-rose-800' },
  { value: 'ESPERANDO_COMPLIANCE', label: 'Esperando Compliance', color: 'bg-orange-100 text-orange-800' },
  { value: 'BLOQUEO_PREVENTIVO', label: 'Bloqueo Preventivo', color: 'bg-red-200 text-red-900' },
  { value: 'EN_VIGILANCIA', label: 'En Vigilancia', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'TESTING', label: 'Testing', color: 'bg-cyan-50 text-cyan-700' },
  { value: 'CERRADO', label: 'Cerrado', color: 'bg-green-100 text-green-800' },
  { value: 'CANCELADO', label: 'Cancelado', color: 'bg-red-100 text-red-800' },
  { value: 'ARCHIVADO', label: 'Archivado', color: 'bg-gray-100 text-gray-800' },
];

const PRIORIDADES = [
  { value: 'CRITICA', label: 'Crítica', color: 'bg-red-100 text-red-800' },
  { value: 'ALTA', label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  { value: 'MEDIA', label: 'Media', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'BAJA', label: 'Baja', color: 'bg-green-100 text-green-800' },
];

const ICON_MAP = {
  ShieldAlert,
  Headphones,
  AlertTriangle,
  Scale,
  Tag,
  Briefcase
};

// Preferencias reutilizables de "Nueva Solicitud desde Caso" (se recuerdan entre casos)
const SOLICITUD_PREF_KEY = 'alertguard_solicitud_prefs';
const SOLICITUD_BASE = {
  tipo_solicitud: '',
  descripcion: 'Actividad sospechosa.',
  prioridad: 'ALTA',
  cerrar_caso_al_completar: true,
  asignar_a: 'grupo',
  grupo_id: '',
  responsable_id: '',
  alertas_seleccionadas: [],
  enviar_email: false,
  email_destinatario: '',
  plantilla_id: '',
  email_asunto: '',
  email_contenido: '',
  bloquear_cuenta: true,
  buscar_email_waya: false,
};
// Campos que se recuerdan para la próxima solicitud (no incluye alertas ni email por-caso)
const SOLICITUD_PREF_FIELDS = [
  'tipo_solicitud', 'descripcion', 'prioridad', 'cerrar_caso_al_completar',
  'asignar_a', 'grupo_id', 'responsable_id', 'enviar_email', 'buscar_email_waya',
  'plantilla_id', 'bloquear_cuenta',
];
const getSolicitudDefaults = () => {
  try {
    const prefs = JSON.parse(localStorage.getItem(SOLICITUD_PREF_KEY) || '{}');
    const picked = {};
    SOLICITUD_PREF_FIELDS.forEach((k) => { if (k in prefs) picked[k] = prefs[k]; });
    return { ...SOLICITUD_BASE, ...picked, alertas_seleccionadas: [], email_asunto: '', email_contenido: '', email_destinatario: '' };
  } catch {
    return { ...SOLICITUD_BASE };
  }
};


const CasoDetalle = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const permCaso = useSeccionPermisos('gestion-de-casos', 'cases');
  const puedeEliminarCaso = !!permCaso?.eliminar;
  const customEstados = useCustomCaseStatuses();
  const ALL_ESTADOS = [...ESTADOS, ...customEstados];
  
  const [caso, setCaso] = useState(null);
  const [cvuInfo, setCvuInfo] = useState(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tiposCaso, setTiposCaso] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Datos detallados de alertas y movimientos
  const [alertasDetalle, setAlertasDetalle] = useState([]);
  const [revalidando, setRevalidando] = useState(false);
  const [verificandoWaya, setVerificandoWaya] = useState(false);
  const [limpiandoWaya, setLimpiandoWaya] = useState(false);
  const [movimientosDetalle, setMovimientosDetalle] = useState([]);
  const [loadingAlertas, setLoadingAlertas] = useState(false);
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  
  // Vista actual para alertas/movimientos
  const [vistaElementos, setVistaElementos] = useState('cards'); // 'cards', 'list', 'timeline'
  
  // Expansión de alerta para ver movimientos
  const [expandedAlertId, setExpandedAlertId] = useState(null);
  
  // Dialogs
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showSearchDialog, setShowSearchDialog] = useState(null); // 'cuenta' | 'movimiento' | 'empresa' | 'alerta'
  const [showDocPreview, setShowDocPreview] = useState(null);
  
  // Alert action dialog
  const [showAlertActionDialog, setShowAlertActionDialog] = useState(false);
  
  // Navegación entre casos
  const [navIds, setNavIds] = useState([]);
  const [navIndex, setNavIndex] = useState(-1);
  const [selectedAlertForAction, setSelectedAlertForAction] = useState(null);
  const [alertActionForm, setAlertActionForm] = useState({ estado: '', comentario: '' });
  
  // Upload
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Note
  const [newNote, setNewNote] = useState({ texto: '', tipo: 'LLAMADA' });
  
  // Comment
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Solicitudes vinculadas
  const [showSolicitudDialog, setShowSolicitudDialog] = useState(false);
  // Clasificación externo/interno
  const [showClasifDialog, setShowClasifDialog] = useState(false);
  const [clasificando, setClasificando] = useState(false);
  const [clasifTipo, setClasifTipo] = useState('interno');
  const [padronQuery, setPadronQuery] = useState('');
  const [padronResults, setPadronResults] = useState([]);
  const [padronLoading, setPadronLoading] = useState(false);
  const [cuentaManual, setCuentaManual] = useState('');

  const buscarPadron = async () => {
    if (!padronQuery.trim()) return;
    setPadronLoading(true);
    try {
      const res = await api.get('/padron-interno', { params: { busqueda: padronQuery.trim(), limit: 15 } });
      setPadronResults(res.data?.clientes || res.data?.items || res.data || []);
    } catch { setPadronResults([]); }
    finally { setPadronLoading(false); }
  };

  const aplicarClasificacion = async (payload) => {
    setClasificando(true);
    try {
      const res = await api.post(`/cases/${caseId}/clasificacion`, payload);
      toast.success(res.data?.message || 'Clasificación actualizada');
      setShowClasifDialog(false);
      setPadronQuery(''); setPadronResults([]); setCuentaManual('');
      loadCaso();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cambiar clasificación');
    } finally { setClasificando(false); }
  };
  const [tiposSolicitud, setTiposSolicitud] = useState([]);
  const [gruposSolicitud, setGruposSolicitud] = useState([]);
  const [solicitudForm, setSolicitudForm] = useState(() => getSolicitudDefaults());
  const [creandoSolicitud, setCreandoSolicitud] = useState(false);
  const [solicitudesVinculadas, setSolicitudesVinculadas] = useState([]);
  const [showVincularDialog, setShowVincularDialog] = useState(false);
  const [solicitudesBusqueda, setSolicitudesBusqueda] = useState([]);
  const [busquedaSolicitud, setBusquedaSolicitud] = useState('');
  const [buscandoSolicitudes, setBuscandoSolicitudes] = useState(false);
  const [plantillasSolicitud, setPlantillasSolicitud] = useState([]);
  const [resolucionesSolicitud, setResolucionesSolicitud] = useState([]);
  const [wayaEmailLoading, setWayaEmailLoading] = useState(false);

  // Envío por Email
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailPlantillas, setEmailPlantillas] = useState([]);
  const [emailForm, setEmailForm] = useState({ email: '', plantilla_id: '', preview: '', asunto: '', source: '' });
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [buscandoWayaEmail, setBuscandoWayaEmail] = useState(false);
  const [enviandoEmail, setEnviandoEmail] = useState(false);

  // Marcar configurada manual (En Vigilancia sin Waya)
  const [showConfigManualDialog, setShowConfigManualDialog] = useState(false);
  const [configManualForm, setConfigManualForm] = useState({ email: '', sucursal_id: '', rubro_id: '' });
  const [configManualRubros, setConfigManualRubros] = useState([]);
  const [guardandoConfigManual, setGuardandoConfigManual] = useState(false);


  // Estados para Investigar
  const [monitoresDisponibles, setMonitoresDisponibles] = useState([]);
  const [monitoresSeleccionados, setMonitoresSeleccionados] = useState([]);
  const [investigando, setInvestigando] = useState(false);
  const [resultadosInvestigacion, setResultadosInvestigacion] = useState([]);
  const [agregandoAlerta, setAgregandoAlerta] = useState(null);
  const [incluirWhitelist, setIncluirWhitelist] = useState(false);
  const [activeTab, setActiveTab] = useState('resumen');

  // Alert states
  const ALERT_STATES = [
    { value: 'NUEVA', label: 'Nueva' },
    { value: 'EN_REVISION', label: 'En Revisión' },
    { value: 'TOMADA', label: 'Tomada' },
    { value: 'INVESTIGANDO', label: 'Investigando' },
    { value: 'ESCALADA', label: 'Escalada' },
    { value: 'RESUELTA', label: 'Resuelta' },
    { value: 'CERRADA', label: 'Cerrada' },
    { value: 'FALSO_POSITIVO', label: 'Falso Positivo' },
  ];

  const openAlertActionDialog = (alerta) => {
    setSelectedAlertForAction(alerta);
    setAlertActionForm({ estado: alerta.estado || 'NUEVA', comentario: '' });
    setShowAlertActionDialog(true);
  };

  const handleAlertAction = async () => {
    if (!selectedAlertForAction) return;
    
    try {
      // Actualizar estado de la alerta
      await api.patch(`/alerts/${selectedAlertForAction.id}`, {
        estado: alertActionForm.estado
      });
      
      // Agregar comentario si hay
      if (alertActionForm.comentario.trim()) {
        await api.post(`/alerts/${selectedAlertForAction.id}/comments`, {
          contenido: alertActionForm.comentario
        });
      }
      
      toast.success('Alerta actualizada');
      setShowAlertActionDialog(false);
      loadAlertasDetalle();
    } catch (error) {
      toast.error('Error al actualizar alerta');
    }
  };

  useEffect(() => {
    loadCaso();
    loadTiposCaso();
    loadUsers();
    loadTiposSolicitud();
    loadGruposSolicitud();
    loadMonitoresDisponibles();
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar IDs de navegación desde backend usando filtros de URL o localStorage
  useEffect(() => {
    const loadNavIds = async () => {
      try {
        // Read filters from URL params first, then localStorage as fallback
        const params = {};
        const urlFilters = searchParams.get('nav_filters');
        let filtersObj = null;

        if (urlFilters) {
          try { filtersObj = JSON.parse(decodeURIComponent(urlFilters)); } catch { /* ignore */ }
        }
        if (!filtersObj) {
          try {
            const stored = localStorage.getItem('caseNavFilters');
            if (stored) filtersObj = JSON.parse(stored);
          } catch { /* ignore */ }
        }

        if (filtersObj) {
          if (filtersObj.estado) params.estado = filtersObj.estado;
          if (filtersObj.prioridad) params.prioridad = filtersObj.prioridad;
          if (filtersObj.asignacion) params.asignacion = filtersObj.asignacion;
          if (filtersObj.cuenta_bloqueada) params.cuenta_bloqueada = filtersObj.cuenta_bloqueada;
          if (filtersObj.search) params.search = filtersObj.search;
          if (filtersObj.sort_by) params.sort_by = filtersObj.sort_by;
          if (filtersObj.sort_order) params.sort_order = filtersObj.sort_order;
          if (filtersObj.tiene_vinculos) params.tiene_vinculos = filtersObj.tiene_vinculos;
          if (filtersObj.es_externo) params.es_externo = filtersObj.es_externo;
          if (filtersObj.ros_en_proceso) params.ros_en_proceso = filtersObj.ros_en_proceso;
          if (filtersObj.waya_verificacion) params.waya_verificacion = filtersObj.waya_verificacion;
          if (filtersObj.fecha_desde) params.fecha_desde = filtersObj.fecha_desde;
          if (filtersObj.fecha_hasta) params.fecha_hasta = filtersObj.fecha_hasta;
          if (filtersObj.cvus) params.cvus = filtersObj.cvus;
          if (filtersObj.solicitud_bloqueo) params.solicitud_bloqueo = filtersObj.solicitud_bloqueo;
          if (filtersObj.min_alertas) {
            if (String(filtersObj.min_alertas).startsWith('exact')) {
              const n = parseInt(String(filtersObj.min_alertas).replace('exact', ''));
              params.min_alertas = n;
              params.max_alertas = n;
            } else {
              params.min_alertas = filtersObj.min_alertas;
            }
          }
        }

        const res = await api.get('/cases/navigation-ids', { params });
        let ids = res.data.ids || [];

        setNavIds(ids);
        setNavIndex(ids.indexOf(caseId));
      } catch {
        // Fallback to localStorage IDs
        try {
          const stored = localStorage.getItem('caseNavIds');
          if (stored) {
            const ids = JSON.parse(stored);
            setNavIds(ids);
            setNavIndex(ids.indexOf(caseId));
          }
        } catch { /* ignore */ }
      }
    };
    loadNavIds();
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const navigateToCaso = (direction) => {
    const newIndex = navIndex + direction;
    if (newIndex >= 0 && newIndex < navIds.length) {
      // Preserve filter params in URL
      const navFilters = searchParams.get('nav_filters');
      const filterParam = navFilters ? `?nav_filters=${navFilters}` : '';
      navigate(`/m/gestion-de-casos/caso/${navIds[newIndex]}${filterParam}`);
    }
  };

  // Cargar solicitudes vinculadas cuando carga el caso
  useEffect(() => {
    if (caseId) {
      loadSolicitudesVinculadas();
    }
  }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar alertas detalladas cuando el caso tenga alertas_ids
  useEffect(() => {
    if (caso?.alertas_ids?.length > 0) {
      loadAlertasDetalle();
    }
  }, [caso?.alertas_ids]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar movimientos detallados cuando el caso tenga movimientos_ids
  useEffect(() => {
    if (caso?.movimientos_ids?.length > 0) {
      loadMovimientosDetalle();
    }
  }, [caso?.movimientos_ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveTitle = async () => {
    const t = (titleValue || '').trim();
    if (t.length < 5) { toast.error('El título debe tener al menos 5 caracteres'); return; }
    setSavingTitle(true);
    try {
      await api.patch(`/cases/${caso.id}`, { titulo: t });
      setCaso(c => ({ ...c, titulo: t }));
      setEditingTitle(false);
      toast.success('Título actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar el título');
    } finally { setSavingTitle(false); }
  };


  const loadCaso = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cases/${caseId}`);
      setCaso(res.data);
      // CVU actual vs anterior (puente por CUIT) — no bloqueante
      api.get(`/cases/${caseId}/cvu-info`).then(r => setCvuInfo(r.data)).catch(() => {});
    } catch (error) {
      toast.error('Error al cargar caso');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadTiposCaso = async () => {
    try {
      const res = await api.get('/cases/config/tipos');
      setTiposCaso(res.data);
    } catch (error) {
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (error) {
    }
  };

  const loadGruposSolicitud = async () => {
    try {
      const res = await api.get('/solicitudes/config/grupos');
      setGruposSolicitud(res.data || []);
    } catch (error) {
    }
  };

  const loadAlertasDetalle = async () => {
    if (!caso?.alertas_ids?.length) return;
    setLoadingAlertas(true);
    try {
      const res = await api.get(`/cases/${caseId}/alerts`);
      setAlertasDetalle(res.data || []);
    } catch (error) {
    } finally {
      setLoadingAlertas(false);
    }
  };

  const loadMovimientosDetalle = async () => {
    if (!caso?.movimientos_ids?.length) return;
    setLoadingMovimientos(true);
    try {
      // Cargar cada movimiento por ID
      const movimientos = await Promise.all(
        caso.movimientos_ids.map(async (id) => {
          try {
            const res = await api.get(`/movimientos/${id}`);
            return res.data;
          } catch {
            return { id, error: true };
          }
        })
      );
      setMovimientosDetalle(movimientos.filter(m => !m.error));
    } catch (error) {
    } finally {
      setLoadingMovimientos(false);
    }
  };

  // Cargar tipos de solicitud disponibles
  const loadTiposSolicitud = async () => {
    try {
      const res = await api.get('/approvals/caso/tipos-solicitud');
      setTiposSolicitud(res.data.tipos || []);
    } catch (error) {
    }
  };

  // Cargar plantillas de email para solicitudes
  const loadPlantillasSolicitud = async () => {
    try {
      const [pltRes, resRes] = await Promise.all([
        api.get('/solicitudes-config/plantillas'),
        api.get('/solicitudes-config/resoluciones'),
      ]);
      setPlantillasSolicitud(pltRes.data || []);
      setResolucionesSolicitud(resRes.data || []);
    } catch (error) {
    }
  };

  const handlePlantillaSolicitudChange = (plantillaId) => {
    const plantilla = plantillasSolicitud.find(p => p.id === plantillaId);
    if (plantilla) {
      const procesarTexto = (texto) => {
        if (!texto || !caso) return '';
        return texto
          .replace(/\{\{cliente_nombre\}\}/g, caso.nombre_padron || caso.nombre_cliente || caso.cuit || '')
          .replace(/\{\{tipo_solicitud\}\}/g, solicitudForm.tipo_solicitud || '')
          .replace(/\{\{descripcion\}\}/g, solicitudForm.descripcion || '')
          .replace(/\{\{numero_referencia\}\}/g, caso.titulo || caso.id?.slice(0, 8) || '');
      };
      setSolicitudForm(prev => ({
        ...prev,
        plantilla_id: plantillaId,
        email_asunto: procesarTexto(plantilla.asunto),
        email_contenido: procesarTexto(plantilla.contenido),
      }));
    }
  };

  // Abrir el modal de nueva solicitud pre-cargando las últimas preferencias usadas
  const openSolicitudDialog = () => {
    loadPlantillasSolicitud();
    setSolicitudForm(getSolicitudDefaults());
    setShowSolicitudDialog(true);
  };

  // Al abrir con una plantilla recordada, regenerar asunto/contenido para ESTE caso
  useEffect(() => {
    if (showSolicitudDialog && solicitudForm.plantilla_id && !solicitudForm.email_contenido
        && plantillasSolicitud.length > 0 && caso) {
      handlePlantillaSolicitudChange(solicitudForm.plantilla_id);
    }
  }, [showSolicitudDialog, plantillasSolicitud, caso]); // eslint-disable-line react-hooks/exhaustive-deps


  // Cargar plantillas para modal de envío directo de email
  useEffect(() => {
    if (showEmailModal && emailPlantillas.length === 0) {
      api.get('/solicitudes-config/plantillas').then(res => {
        setEmailPlantillas(res.data || []);
      }).catch(() => {});
    }
    if (showEmailModal && caso) {
      // Precargar el email que se enviaría (armado desde los datos del caso)
      setEmailPreviewLoading(true);
      api.get(`/cases/${caso.id}/email-preview`).then(res => {
        const d = res.data || {};
        setEmailForm({ email: d.to_email || '', plantilla_id: '', preview: d.contenido || '', asunto: d.asunto || '', source: d.source || '' });
      }).catch(() => {
        const lastEmail = [...(caso.timeline || [])].reverse().find(t =>
          (t.tipo === 'EMAIL_ENVIADO' || t.tipo === 'EMAIL_REENVIADO') && t.metadata?.destinatario);
        const solEmail = solicitudesVinculadas?.find(s => s.email_destinatario)?.email_destinatario;
        setEmailForm({ email: lastEmail?.metadata?.destinatario || solEmail || '', plantilla_id: '', preview: '', asunto: '', source: '' });
      }).finally(() => setEmailPreviewLoading(false));
    }
  }, [showEmailModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Buscar el email del cliente en Waya (cuando no se encontró internamente)
  const buscarEmailWaya = async () => {
    if (!caso) return;
    setBuscandoWayaEmail(true);
    try {
      const res = await api.get(`/cases/${caso.id}/email-preview?buscar_waya=true`);
      const d = res.data || {};
      if (d.to_email) {
        setEmailForm(f => ({ ...f, email: d.to_email }));
        toast.success(`Email encontrado en Waya: ${d.to_email}`);
      } else {
        toast.warning('No se encontró el email del cliente en Waya');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al buscar en Waya');
    } finally {
      setBuscandoWayaEmail(false);
    }
  };

  const handleEmailPlantillaChange = (plantillaId) => {
    const plt = emailPlantillas.find(p => p.id === plantillaId);
    if (plt && caso) {
      const proc = (t) => (t || '')
        .replace(/\{\{cliente_nombre\}\}/g, caso.nombre_padron || caso.cuit || '')
        .replace(/\{\{numero_referencia\}\}/g, caso.titulo || '')
        .replace(/\{\{descripcion\}\}/g, caso.descripcion || '')
        .replace(/\{\{cuit\}\}/g, caso.cuit || '');
      setEmailForm(f => ({ ...f, plantilla_id: plantillaId, preview: proc(plt.contenido), asunto: proc(plt.asunto) }));
    }
  };

  const handleEnviarEmailModal = async () => {
    if (!emailForm.email || !emailForm.email.includes('@')) { toast.warning('Ingresá un email válido'); return; }
    if (!emailForm.preview) { toast.warning('El contenido del email no puede estar vacío'); return; }
    try {
      const res = await api.post(`/cases/${caso.id}/reenviar-email`, {
        email_override: emailForm.email,
        plantilla_contenido: emailForm.preview,
        plantilla_asunto: emailForm.asunto,
      });
      toast.success(res.data.message || 'Email enviado');
      setShowEmailModal(false);
      loadCaso();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al enviar'); }
  };

  // Obtener identificador del caso (CVU o CUIT)
  const getCasoCvu = () => {
    return caso?.cvu_principal || caso?.cuentas_relacionadas?.[0] || caso?.cuit || null;
  };

  // Detectar si es CVU (22 dígitos) o CUIT
  const detectTipoBusqueda = (valor) => {
    if (!valor) return 'cvu';
    const limpio = valor.replace(/-/g, '').replace(/ /g, '');
    if (limpio.length >= 20) return 'cvu';
    return 'cuit';
  };

  // Cuando cambia el tipo de solicitud, ajustar defaults
  const handleTipoSolicitudChange = async (tipo) => {
    const isBloqueo = tipo === 'BLOQUEO_CUENTA';
    setSolicitudForm(prev => ({
      ...prev,
      tipo_solicitud: tipo,
      bloquear_cuenta: isBloqueo,
      enviar_email: isBloqueo ? true : prev.enviar_email,
      buscar_email_waya: isBloqueo, // Auto-buscar email en Waya en background
    }));
  };

  // Cargar solicitudes vinculadas al caso
  const loadSolicitudesVinculadas = async () => {
    if (!caseId) return;
    try {
      const res = await api.get(`/approvals/caso/${caseId}/solicitudes`);
      setSolicitudesVinculadas(res.data.solicitudes || []);
    } catch (error) {
    }
  };

  // Cargar monitores disponibles para investigar
  const loadMonitoresDisponibles = async () => {
    try {
      const res = await api.get('/monitors?activo=true');
      setMonitoresDisponibles(res.data || []);
    } catch (error) {
    }
  };

  // Investigar desde caso - Ejecutar monitores seleccionados
  const handleInvestigar = async () => {
    if (monitoresSeleccionados.length === 0) {
      toast.error('Seleccione al menos un monitor para investigar');
      return;
    }

    setInvestigando(true);
    setResultadosInvestigacion([]);
    
    try {
      const res = await api.post(`/monitors/investigar-desde-caso/${caseId}`, {
        monitor_ids: monitoresSeleccionados,
        incluir_whitelist: incluirWhitelist
      });
      
      setResultadosInvestigacion(res.data.resultados || []);
      
      if (res.data.total_coincidencias === 0) {
        const whitelistMsg = res.data.whitelist_excluida ? ' (whitelist excluida)' : '';
        toast.info(`No se encontraron coincidencias${whitelistMsg}`);
      } else {
        const whitelistMsg = res.data.whitelist_excluida ? ' (whitelist excluida)' : '';
        toast.success(`Se encontraron ${res.data.total_coincidencias} coincidencias${whitelistMsg}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al ejecutar investigación';
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setInvestigando(false);
    }
  };

  // Agregar coincidencia de investigación al caso
  const handleAgregarAlertaInvestigacion = async (resultado) => {
    setAgregandoAlerta(resultado.monitor_id + resultado.cuenta_id);
    
    try {
      const res = await api.post(`/monitors/agregar-alerta-caso/${caseId}`, resultado);
      setCaso(res.data);
      toast.success('Alerta agregada al caso');
      
      // Remover de resultados de investigación
      setResultadosInvestigacion(prev => 
        prev.filter(r => !(r.monitor_id === resultado.monitor_id && r.cuenta_id === resultado.cuenta_id))
      );
    } catch (error) {
      toast.error('Error al agregar alerta');
    } finally {
      setAgregandoAlerta(null);
    }
  };

  // Toggle selección de monitor para investigar
  const toggleMonitorSeleccion = (monitorId) => {
    setMonitoresSeleccionados(prev => 
      prev.includes(monitorId) 
        ? prev.filter(id => id !== monitorId)
        : [...prev, monitorId]
    );
  };

  // Seleccionar/deseleccionar todos los monitores
  const toggleTodosMonitores = () => {
    if (monitoresSeleccionados.length === monitoresDisponibles.length) {
      setMonitoresSeleccionados([]);
    } else {
      setMonitoresSeleccionados(monitoresDisponibles.map(m => m.id));
    }
  };

  // Crear solicitud desde caso
  const handleCrearSolicitud = async () => {
    if (!solicitudForm.tipo_solicitud || !solicitudForm.descripcion) {
      toast.error('Complete todos los campos requeridos');
      return;
    }
    
    setCreandoSolicitud(true);
    try {
      // Construir resumen de alertas seleccionadas
      let alertasResumen = [];
      if (solicitudForm.alertas_seleccionadas.length > 0) {
        const allAlertas = caso.alertas || alertasDetalle || [];
        alertasResumen = solicitudForm.alertas_seleccionadas.map(alertaId => {
          const alerta = allAlertas.find(a => (a.id || a.alerta_id) === alertaId);
          if (!alerta) return { id: alertaId };
          return {
            id: alertaId,
            titulo: alerta.titulo || alerta.title || alerta.tipo_alerta || '',
            descripcion: alerta.descripcion || alerta.description || '',
            severidad: alerta.severidad || alerta.severity || '',
            estado: alerta.estado || alerta.status || '',
            tipo: alerta.tipo || alerta.type || alerta.tipo_alerta || '',
            fecha: alerta.fecha || alerta.created_at || '',
            monto: alerta.monto_total || alerta.monto || '',
            movimientos_count: alerta.movimientos_ids?.length || alerta.cantidad_movimientos || 0
          };
        });
      }

      const payload = {
        tipo_solicitud: solicitudForm.tipo_solicitud,
        descripcion: solicitudForm.descripcion,
        prioridad: solicitudForm.prioridad,
        cerrar_caso_al_completar: solicitudForm.cerrar_caso_al_completar,
        grupo_id: solicitudForm.asignar_a === 'grupo' ? solicitudForm.grupo_id : null,
        responsable_id: solicitudForm.asignar_a === 'persona' ? solicitudForm.responsable_id : null,
        alertas_adjuntas: alertasResumen.length > 0 ? alertasResumen : undefined,
        enviar_email: solicitudForm.enviar_email,
        buscar_email_waya: solicitudForm.buscar_email_waya || false,
        email_destinatario: solicitudForm.enviar_email ? (solicitudForm.email_destinatario || null) : null,
        plantilla_id: solicitudForm.enviar_email ? solicitudForm.plantilla_id : null,
        email_asunto: solicitudForm.enviar_email ? solicitudForm.email_asunto : null,
        email_contenido: solicitudForm.enviar_email ? solicitudForm.email_contenido : null,
        cvu_bloqueo: (solicitudForm.tipo_solicitud === 'BLOQUEO_CUENTA' && solicitudForm.bloquear_cuenta) ? getCasoCvu() : null,
        tipo_busqueda_bloqueo: (solicitudForm.tipo_solicitud === 'BLOQUEO_CUENTA' && solicitudForm.bloquear_cuenta) ? detectTipoBusqueda(getCasoCvu()) : null,
      };
      
      const res = await api.post(`/approvals/caso/${caseId}`, payload);
      let msg = res.data.message;
      if (res.data.waya_result) {
        if (res.data.waya_result.en_background) {
          toast.info('El bloqueo de cuenta se ejecutará en segundo plano. Recibirás una notificación cuando termine.', { duration: 6000 });
        } else if (res.data.waya_result.success) {
          msg += ' | Cuenta bloqueada en Waya exitosamente';
        } else {
          toast.error('Error al bloquear cuenta en Waya: ' + (res.data.waya_result.error || 'Error desconocido'));
        }
      }
      if (res.data.email_result && res.data.email_result.en_background) {
        toast.info('El email se enviará en segundo plano. Recibirás una notificación cuando se envíe.', { duration: 5000 });
      }
      toast.success(msg);
      // Recordar las preferencias usadas para la próxima solicitud
      try {
        const prefs = {};
        SOLICITUD_PREF_FIELDS.forEach((k) => { prefs[k] = solicitudForm[k]; });
        localStorage.setItem(SOLICITUD_PREF_KEY, JSON.stringify(prefs));
      } catch { /* ignore */ }
      setShowSolicitudDialog(false);
      setSolicitudForm(getSolicitudDefaults());
      // Recargar solicitudes y caso
      loadSolicitudesVinculadas();
      loadCaso();
    } catch (error) {
      toast.error('Error al crear la solicitud');
      console.error(error);
    } finally {
      setCreandoSolicitud(false);
    }
  };

  // Buscar solicitudes para vincular
  const buscarSolicitudesParaVincular = async (search) => {
    setBusquedaSolicitud(search);
    if (search.length < 2) { setSolicitudesBusqueda([]); return; }
    setBuscandoSolicitudes(true);
    try {
      const res = await api.get(`/solicitudes?search=${encodeURIComponent(search)}&limit=10`);
      const data = res.data?.solicitudes || res.data || [];
      // Filter out already linked ones
      const linked = new Set((caso?.solicitudes_vinculadas || []).map(s => s.id));
      setSolicitudesBusqueda(data.filter(s => !linked.has(s.id)));
    } catch { setSolicitudesBusqueda([]); }
    finally { setBuscandoSolicitudes(false); }
  };

  // Vincular solicitud existente
  const vincularSolicitud = async (solicitudId) => {
    try {
      await api.post(`/cases/${caso.id}/vincular-solicitud`, { solicitud_id: solicitudId });
      toast.success('Solicitud vinculada al caso');
      setShowVincularDialog(false);
      setBusquedaSolicitud('');
      setSolicitudesBusqueda([]);
      loadSolicitudesVinculadas();
      loadCaso();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al vincular solicitud');
    }
  };

  // Desvincular solicitud
  const desvincularSolicitud = async (solicitudId) => {
    if (!window.confirm('¿Está seguro de desvincular esta solicitud del caso?')) return;
    try {
      await api.post(`/cases/${caso.id}/desvincular-solicitud/${solicitudId}`);
      toast.success('Solicitud desvinculada del caso');
      loadSolicitudesVinculadas();
      loadCaso();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desvincular solicitud');
    }
  };

  const getEstadoStyle = (estado) => {
    return ALL_ESTADOS.find(e => e.value === estado) || ESTADOS[0];
  };

  const getPrioridadStyle = (prioridad) => {
    return PRIORIDADES.find(p => p.value === prioridad) || PRIORIDADES[2];
  };

  const getTipoInfo = (tipoCodigo) => {
    const tipo = tiposCaso.find(t => t.codigo === tipoCodigo);
    if (tipo) {
      const IconComponent = ICON_MAP[tipo.icono] || Tag;
      return { ...tipo, Icon: IconComponent };
    }
    return { codigo: tipoCodigo, nombre: tipoCodigo, color: '#64748b', Icon: Tag };
  };

  // ============== Estado y Prioridad ==============
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeTemplates, setCloseTemplates] = useState([]);
  const [closeReason, setCloseReason] = useState('');
  const [closeTipoResolucion, setCloseTipoResolucion] = useState('');
  const [pendingCloseState, setPendingCloseState] = useState(null);
  const [tiposResolucion, setTiposResolucion] = useState([]);

  // Load resolution templates and tipos
  useEffect(() => {
    api.get('/cases/resolution-templates').then(r => setCloseTemplates(r.data)).catch(() => {});
    api.get('/cases/tipos-resolucion').then(r => setTiposResolucion(r.data)).catch(() => {});
  }, []);

  const handleChangeEstado = async (nuevoEstado) => {
    // If closing, show dialog with templates
    if (['CERRADO', 'BLOQUEO_PREVENTIVO'].includes(nuevoEstado)) {
      setPendingCloseState(nuevoEstado);
      setCloseReason('');
      setShowCloseDialog(true);
      return;
    }
    try {
      const res = await api.patch(`/cases/${caseId}`, { estado: nuevoEstado });
      setCaso(res.data);
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const openConfigManualDialog = async () => {
    setConfigManualForm({
      email: caso?.vigilancia_email || '',
      sucursal_id: caso?.vigilancia_sucursal_id || '',
      rubro_id: caso?.vigilancia_rubro_id || '',
    });
    setShowConfigManualDialog(true);
    if (configManualRubros.length === 0) {
      try {
        const r = await api.get('/waya/rubros');
        setConfigManualRubros(r.data?.rubros || []);
      } catch { /* opcional */ }
    }
  };

  const handleConfigManualSubmit = async () => {
    setGuardandoConfigManual(true);
    try {
      await api.post(`/cases/${caseId}/vigilancia-manual`, {
        email: configManualForm.email.trim() || null,
        sucursal_id: configManualForm.sucursal_id.trim() || null,
        rubro_id: configManualForm.rubro_id || null,
      });
      toast.success('Caso marcado En Vigilancia (configurada manualmente)');
      setShowConfigManualDialog(false);
      loadCaso();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo marcar el caso');
    } finally {
      setGuardandoConfigManual(false);
    }
  };

  const handleRevertirVigilancia = async () => {
    if (!window.confirm('¿Revertir la marca "En Vigilancia (manual)" y restaurar el estado anterior?')) return;
    try {
      const res = await api.post(`/cases/${caseId}/vigilancia-manual/revertir`);
      toast.success(`Vigilancia revertida. Estado: ${res.data?.estado || 'anterior'}`);
      loadCaso();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo revertir');
    }
  };


  const handleConfirmClose = async () => {
    try {
      const resolucion = closeTipoResolucion ? `[${closeTipoResolucion}] ${closeReason}` : closeReason;
      const res = await api.patch(`/cases/${caseId}`, {
        estado: pendingCloseState,
        observaciones_cierre: closeReason,
        tipo_resolucion: closeTipoResolucion,
        resolucion: resolucion,
      });
      setCaso(res.data);
      toast.success('Estado actualizado');
      setShowCloseDialog(false);
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleChangePrioridad = async (nuevaPrioridad) => {
    try {
      const res = await api.patch(`/cases/${caseId}`, { prioridad: nuevaPrioridad });
      setCaso(res.data);
      toast.success('Prioridad actualizada');
    } catch (error) {
      toast.error('Error al cambiar prioridad');
    }
  };

  const handleAssign = async (asignadoA) => {
    // Si ya está asignado y es una reasignación, pedir confirmación
    const currentAssignee = caso.responsable_nombre || caso.asignado_a;
    const newUser = users.find(u => u.id === asignadoA);
    const newName = newUser?.nombre || newUser?.email || asignadoA;
    
    if (currentAssignee && currentAssignee !== newName) {
      if (!window.confirm(`¿Reasignar de "${currentAssignee}" a "${newName}"?`)) return;
    }
    
    try {
      const res = await api.patch(`/cases/${caseId}`, {
        responsable_id: asignadoA,
        responsable_nombre: newName
      });
      setCaso(res.data);
      toast.success(`Caso asignado a ${newName}`);
    } catch (error) {
      toast.error('Error al asignar caso');
    }
  };

  // ============== Documentos con Drag & Drop ==============
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    addFilesToUpload(files);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFilesToUpload(files);
  };

  const addFilesToUpload = (files) => {
    const newFiles = files.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      nombre: file.name,
      tipo: 'ADJUNTO',
      uploading: false,
      uploaded: false,
      error: null
    }));
    setUploadingFiles(prev => [...prev, ...newFiles]);
  };

  const removeFileFromUpload = (fileId) => {
    setUploadingFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const updateFileData = (fileId, field, value) => {
    setUploadingFiles(prev => prev.map(f => 
      f.id === fileId ? { ...f, [field]: value } : f
    ));
  };

  const uploadDocuments = async () => {
    const filesToUpload = uploadingFiles.filter(f => !f.uploaded);
    
    for (const fileData of filesToUpload) {
      updateFileData(fileData.id, 'uploading', true);
      
      try {
        const fakeUrl = `https://storage.example.com/cases/${caseId}/${fileData.file.name}`;
        
        await api.post(`/cases/${caseId}/documents`, {
          nombre: fileData.nombre,
          tipo: fileData.tipo,
          url: fakeUrl
        });
        
        updateFileData(fileData.id, 'uploaded', true);
        updateFileData(fileData.id, 'uploading', false);
      } catch (error) {
        updateFileData(fileData.id, 'error', 'Error al subir');
        updateFileData(fileData.id, 'uploading', false);
      }
    }
    
    await loadCaso();
    toast.success('Documentos subidos');
    setShowUploadDialog(false);
    setUploadingFiles([]);
  };

  const handleDeleteDocumento = async (docId) => {
    if (!window.confirm('¿Eliminar este documento?')) return;
    
    try {
      const res = await api.delete(`/cases/${caseId}/documentos/${docId}`);
      setCaso(res.data);
      toast.success('Documento eliminado');
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  // ============== Notas ==============
  const handleAddNote = async () => {
    if (!newNote.texto.trim()) return;
    
    try {
      const res = await api.post(`/cases/${caseId}/notes`, {
        texto: newNote.texto,
        tipo_nota: newNote.tipo
      });
      setCaso(res.data);
      setShowNoteDialog(false);
      setNewNote({ texto: '', tipo: 'LLAMADA' });
      toast.success('Nota agregada');
    } catch (error) {
      toast.error('Error al agregar nota');
    }
  };

  // ============== Comentarios ==============
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    setAddingComment(true);
    try {
      const res = await api.post(`/cases/${caseId}/comments`, {
        texto: newComment
      });
      setCaso(res.data);
      setNewComment('');
      toast.success('Comentario agregado');
    } catch (error) {
      toast.error('Error al agregar comentario');
    } finally {
      setAddingComment(false);
    }
  };

  // ============== Búsqueda y Vinculación ==============
  const handleSearch = useCallback(async (tipo) => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    
    setSearching(true);
    try {
      let endpoint = '';
      if (tipo === 'cuenta') {
        endpoint = `/padron?search=${encodeURIComponent(searchTerm)}&limit=10`;
      } else if (tipo === 'movimiento') {
        endpoint = `/movimientos?search=${encodeURIComponent(searchTerm)}&limit=10`;
      } else if (tipo === 'empresa') {
        endpoint = `/empresas?search=${encodeURIComponent(searchTerm)}&limit=10`;
      } else if (tipo === 'alerta') {
        endpoint = `/alerts?search=${encodeURIComponent(searchTerm)}&limit=10`;
      }
      
      const res = await api.get(endpoint);
      setSearchResults(res.data?.data || res.data || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (showSearchDialog) {
      const timer = setTimeout(() => handleSearch(showSearchDialog), 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, showSearchDialog, handleSearch]);

  const handleAddCuenta = async (cuit) => {
    try {
      const res = await api.post(`/cases/${caseId}/accounts`, { cuentas: [cuit] });
      setCaso(res.data);
      toast.success('Cuenta vinculada');
    } catch (error) {
      toast.error('Error al vincular cuenta');
    }
  };

  const handleRemoveCuenta = async (cuit) => {
    try {
      const res = await api.delete(`/cases/${caseId}/cuentas/${cuit}`);
      setCaso(res.data);
      toast.success('Cuenta desvinculada');
    } catch (error) {
      toast.error('Error al desvincular cuenta');
    }
  };

  const handleAddAlerta = async (alertaId) => {
    try {
      const res = await api.post(`/cases/${caseId}/alerts`, { alertas_ids: [alertaId] });
      setCaso(res.data);
      toast.success('Alerta vinculada');
    } catch (error) {
      toast.error('Error al vincular alerta');
    }
  };

  const handleRemoveAlerta = async (alertaId) => {
    try {
      const res = await api.delete(`/cases/${caseId}/alerts/${alertaId}`);
      setCaso(res.data);
      setAlertasDetalle(prev => prev.filter(a => a.id !== alertaId));
      toast.success('Alerta desvinculada');
    } catch (error) {
      toast.error('Error al desvincular alerta');
    }
  };

  const handleAddMovimiento = async (movId) => {
    try {
      const res = await api.post(`/cases/${caseId}/movimientos`, { movimientos_ids: [movId] });
      setCaso(res.data);
      toast.success('Movimiento vinculado');
    } catch (error) {
      toast.error('Error al vincular movimiento');
    }
  };

  const handleRemoveMovimiento = async (movId) => {
    try {
      const res = await api.delete(`/cases/${caseId}/movimientos/${movId}`);
      setCaso(res.data);
      setMovimientosDetalle(prev => prev.filter(m => m.id !== movId && m.id_coelsa !== movId));
      toast.success('Movimiento desvinculado');
    } catch (error) {
      toast.error('Error al desvincular movimiento');
    }
  };

  const handleAddEmpresa = async (empresaId) => {
    try {
      const res = await api.post(`/cases/${caseId}/empresas`, { empresa_id: empresaId });
      setCaso(res.data);
      toast.success('Empresa vinculada');
    } catch (error) {
      toast.error('Error al vincular empresa');
    }
  };

  const handleRemoveEmpresa = async (empresaId) => {
    try {
      const res = await api.delete(`/cases/${caseId}/empresas/${empresaId}`);
      setCaso(res.data);
      toast.success('Empresa desvinculada');
    } catch (error) {
      toast.error('Error al desvincular empresa');
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <RefreshCw className="animate-spin text-purple-600" size={40} />
      </div>
    );
  }

  if (!caso) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Caso no encontrado</p>
      </div>
    );
  }

  const estadoStyle = getEstadoStyle(caso.estado);
  const prioridadStyle = getPrioridadStyle(caso.prioridad);
  const tipoInfo = getTipoInfo(caso.tipo);
  const TipoIcon = tipoInfo.Icon;

  return (
    <div className="p-6 lg:p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate('/m/gestion-de-casos/casos')}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
            data-testid="volver-a-casos-btn"
          >
            <ArrowLeft size={20} />
            <span>Volver a casos</span>
          </button>
          
          {navIds.length > 1 && (
            <div className="flex items-center gap-2 absolute left-1/2 transform -translate-x-1/2 z-10 bg-white/90 backdrop-blur px-4 py-1.5 rounded-full shadow-sm border border-slate-200" data-testid="case-navigation">
              <button
                onClick={() => navigateToCaso(-1)}
                disabled={navIndex <= 0}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${navIndex <= 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                data-testid="nav-prev-case"
              >
                <ChevronLeft size={16} />
                Anterior
              </button>
              <span className="text-xs text-slate-400 tabular-nums">
                {navIndex + 1} / {navIds.length}
              </span>
              <button
                onClick={() => navigateToCaso(1)}
                disabled={navIndex >= navIds.length - 1}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${navIndex >= navIds.length - 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`}
                data-testid="nav-next-case"
              >
                Siguiente
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
        
        {/* Badge de reactivación destacado */}
        {caso.reactivado_desde_vigilancia && caso.estado === 'ABIERTO' && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-amber-50 border border-amber-300 rounded-lg" data-testid="reactivation-banner">
            <Zap size={18} className="text-amber-600 animate-pulse" />
            <div>
              <span className="text-sm font-bold text-amber-800">Caso reactivado desde vigilancia</span>
              <span className="text-xs text-amber-600 ml-2">
                Se detectaron nuevas alertas de monitores automáticos
                {caso.fecha_reactivacion && ` el ${new Date(caso.fecha_reactivacion).toLocaleString('es-AR')}`}
              </span>
            </div>
          </div>
        )}

        {/* Banner predictivo: Comportamiento escalando */}
        <EscalationBanner caseId={caso.id} />

        {/* Banner de casos anteriores / reincidencia */}
        <PreviousCasesBanner
          casos={caso.casos_anteriores}
          caseId={caso.id}
          contextoIncorporado={caso.contexto_incorporado || []}
          onIncorporated={() => loadCaso()}
        />

        {caso.episodios?.length > 1 && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4" data-testid="episodios-timeline">
            <div className="flex items-center gap-2 mb-3">
              <Zap size={16} className="text-violet-600" />
              <h3 className="text-sm font-semibold text-violet-900">
                Reincidencias del cliente ({caso.episodios.length} episodios)
              </h3>
            </div>
            <ol className="relative border-l-2 border-violet-200 ml-2 space-y-3">
              {[...caso.episodios].reverse().map((ep, idx) => (
                <li key={ep.id || idx} className="ml-4" data-testid={`episodio-${idx}`}>
                  <span className="absolute -left-[7px] w-3 h-3 rounded-full bg-violet-400 border-2 border-white" />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-slate-700">
                      {ep.fecha ? new Date(ep.fecha).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white border border-violet-200 text-violet-700">
                      {ep.alertas_count} alerta(s)
                    </span>
                    {ep.severidad_max && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200">
                        {ep.severidad_max}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{ep.descripcion}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
        
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="p-2 rounded-lg" 
                style={{ backgroundColor: `${tipoInfo.color}20` }}
              >
                <TipoIcon size={24} style={{ color: tipoInfo.color }} />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  {editingTitle ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        className="text-xl font-bold h-9 w-[320px] sm:w-[420px]"
                        autoFocus
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                        data-testid="case-title-edit-input"
                      />
                      <Button size="sm" className="h-8 px-2" onClick={handleSaveTitle} disabled={savingTitle} data-testid="case-title-save-btn">
                        <CheckCircle size={16} />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setEditingTitle(false)} disabled={savingTitle} data-testid="case-title-cancel-btn">
                        <X size={16} />
                      </Button>
                    </div>
                  ) : (
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 group" data-testid="case-title">
                      {caso.titulo}
                      <button
                        onClick={() => { setTitleValue(caso.titulo || ''); setEditingTitle(true); }}
                        title="Editar título del caso"
                        className="opacity-60 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity text-slate-400 hover:text-blue-600"
                        data-testid="case-title-edit-btn"
                      >
                        <Edit2 size={16} />
                      </button>
                    </h1>
                  )}
                  {caso.reincidencias_count > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-violet-100 text-violet-700 border border-violet-200"
                      title="El cliente volvió a generar alertas sobre este mismo caso"
                      data-testid="reincidente-badge"
                    >
                      <Zap size={12} /> Reincidente x{caso.reincidencias_count}
                    </span>
                  )}
                  {caso.waya_verificacion?.resultado && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${
                        caso.waya_verificacion.es_bloqueada_waya ? 'bg-orange-100 text-orange-800 border-orange-300'
                        : caso.waya_verificacion.resultado === 'CON_CVU' ? 'bg-red-100 text-red-800 border-red-300'
                        : caso.waya_verificacion.resultado === 'SIN_CVU' ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                        : caso.waya_verificacion.resultado === 'ACTIVA_INFO' ? 'bg-slate-100 text-slate-700 border-slate-300'
                        : 'bg-zinc-100 text-zinc-600 border-zinc-300'}`}
                      title={`${caso.waya_verificacion.motivo || ''}${caso.waya_verificacion.fecha ? ' · ' + new Date(caso.waya_verificacion.fecha).toLocaleString('es-AR') : ''}`}
                      data-testid="waya-verif-badge"
                    >
                      <Shield size={12} />
                      {caso.waya_verificacion.es_bloqueada_waya ? 'Bloqueada en Waya'
                        : caso.waya_verificacion.resultado === 'CON_CVU' ? '⚠️ Debía baja y activa'
                        : caso.waya_verificacion.resultado === 'SIN_CVU' ? 'Waya: Sin CVU'
                        : caso.waya_verificacion.resultado === 'ACTIVA_INFO' ? 'Waya: Activa'
                        : 'Waya: Error'}
                    </span>
                  )}
                </div>
                {(caso.nombre_padron || caso.cvu_principal || caso.cuit) && (
                  <div className="flex items-center gap-2 mt-1">
                    <User size={14} className="text-blue-500" />
                    {caso.nombre_padron && (
                      <span className="text-sm font-medium text-blue-700">{caso.nombre_padron}</span>
                    )}
                    {caso.es_externo ? (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200" title="CVU externo (no pertenece al padrón interno)">
                        Externo
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200" title="Pertenece al padrón interno">
                        Interno
                      </span>
                    )}
                    <button
                      onClick={() => { setClasifTipo(caso.es_externo ? 'interno' : 'externo'); setShowClasifDialog(true); }}
                      className="text-[10px] text-slate-500 hover:text-blue-600 underline"
                      data-testid="cambiar-clasificacion-btn"
                      title="Cambiar entre externo/interno y asignar cuenta"
                    >
                      cambiar
                    </button>
                    {(caso.cvu_principal || caso.cuit || caso.cuentas_relacionadas?.[0]) && (
                      <span className="text-xs text-slate-400 font-mono">
                        ({caso.cvu_principal || caso.cuit || caso.cuentas_relacionadas?.[0]})
                      </span>
                    )}
                    {caso.cuit && (caso.cuit.replace(/\D/g, '').length === 11) && (
                      <span className="text-xs text-slate-600 font-mono bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5" data-testid="case-detail-cuit">
                        CUIT: {caso.cuit}
                      </span>
                    )}
                    {cvuInfo?.cambio_cvu && (
                      <span
                        className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 font-mono"
                        title={`El CVU del titular cambió. El ROS se carga con el CVU del caso (${cvuInfo.cvu_caso}); el bloqueo/cierre en Waya se hace sobre el CVU actual (${cvuInfo.cvu_actual}).`}
                        data-testid="cvu-cambio-badge"
                      >
                        CVU actual: {cvuInfo.cvu_actual} · antes: {cvuInfo.cvu_caso}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge className={estadoStyle.color}>{estadoStyle.label}</Badge>
              <Badge className={prioridadStyle.color}>Prioridad: {prioridadStyle.label}</Badge>
              <Badge style={{ backgroundColor: `${tipoInfo.color}20`, color: tipoInfo.color }}>
                {tipoInfo.nombre}
              </Badge>
              {caso.ros_uif_nro && caso.ros_id_presentado && (
                <button
                  onClick={() => navigate(`/m/gestion-de-casos/ros-detalle/${caso.ros_id_presentado}`)}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200 hover:bg-green-200 transition-colors"
                  data-testid="caso-uif-badge"
                  title="Ver el ROS presentado en UIF"
                >
                  <Shield size={12} /> ROS UIF Nº {caso.ros_uif_nro}
                </button>
              )}
              {caso.asignado_a && (
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <User size={14} />
                  {caso.asignado_a}
                </span>
              )}
            </div>
            {caso.descripcion && (
              <p className="text-sm text-slate-600 mt-2">{caso.descripcion}</p>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            <Select value={caso.estado} onValueChange={handleChangeEstado}>
              <SelectTrigger className="w-44" style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ALL_ESTADOS.map(e => (
                  <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {caso.cuenta_bloqueada && (
              <div className="flex items-center gap-1">
                <span className="px-2 py-1 rounded text-xs font-semibold bg-red-600 text-white inline-flex items-center gap-1" data-testid="cuenta-bloqueada-chip">
                  <Lock size={12} />
                  Cuenta Bloqueada
                </span>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50"
                  data-testid="quitar-bloqueo-btn"
                  title="Quitar tag de bloqueo"
                  onClick={async () => {
                    if (!window.confirm('¿Quitar el tag de bloqueo de este caso?\n\nEsto NO desbloquea la cuenta en Waya, solo quita la marca en el sistema.')) return;
                    try {
                      await api.post(`/cases/${caso.id}/quitar-bloqueo`);
                      toast.success('Tag de bloqueo removido');
                      loadCaso();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
                  }}>
                  <XCircle size={14} />
                </Button>
              </div>
            )}
            {!caso.cuenta_bloqueada && (
              <Button variant="outline" size="sm" className="h-8 text-xs text-red-600 border-red-300 hover:bg-red-50"
                data-testid="marcar-bloqueada-btn"
                onClick={async () => {
                  if (!window.confirm('¿Marcar esta cuenta como bloqueada manualmente?\n\nEsto cambiará el estado del caso a "Esperando Compliance".')) return;
                  try {
                    await api.post(`/cases/${caso.id}/marcar-bloqueada`);
                    toast.success('Cuenta marcada como bloqueada. Estado: Esperando Compliance');
                    loadCaso();
                  } catch (e) { toast.error(e.response?.data?.detail || 'Error al marcar como bloqueada'); }
                }}>
                <Lock size={12} className="mr-1" />
                Marcar Bloqueada
              </Button>
            )}

            {caso.estado !== 'EN_VIGILANCIA' && (
              <Button variant="outline" size="sm" className="h-8 text-xs text-cyan-700 border-cyan-300 hover:bg-cyan-50"
                data-testid="marcar-configurada-btn"
                onClick={openConfigManualDialog}>
                <CheckCircle size={12} className="mr-1" />
                Marcar Configurada
              </Button>
            )}
            {caso.estado === 'EN_VIGILANCIA' && caso.vigilancia_manual && (
              <Button variant="outline" size="sm" className="h-8 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="revertir-vigilancia-btn"
                onClick={handleRevertirVigilancia}>
                <RefreshCw size={12} className="mr-1" />
                Revertir Vigilancia
              </Button>
            )}
            
            {/* Botón enviar/reenviar email (siempre disponible) */}
            <Button variant="outline" size="sm" className="h-8 text-xs text-teal-600 border-teal-300 hover:bg-teal-50"
              data-testid="reenviar-email-btn"
              onClick={() => setShowEmailModal(true)}>
              <Send size={12} className="mr-1" />
              {caso.timeline?.some(t => t.tipo === 'EMAIL_ENVIADO' || t.tipo === 'EMAIL_REENVIADO') ? 'Reenviar Email' : 'Enviar Email'}
            </Button>

            <CasoExportPDF casoId={caso.id} casoTitulo={caso.titulo} />

            <Button variant="outline" size="sm" className="h-8 text-xs text-amber-600 border-amber-300 hover:bg-amber-50 gap-1"
              data-testid="refresh-monitors-btn"
              onClick={async () => {
                try {
                  const res = await api.post(`/cases/${caso.id}/refresh-monitors`);
                  const d = res.data;
                  if (d.removed > 0) {
                    toast.success(`${d.removed} monitor(es) duplicado(s) unificado(s). ${d.final} alertas restantes.`);
                  } else {
                    toast.info('No se encontraron monitores duplicados');
                  }
                  loadCaso();
                } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
              }}>
              <RefreshCw size={12} />
              Actualizar Monitores
            </Button>

            <Button variant="outline" size="sm" disabled={revalidando}
              className="h-8 text-xs text-indigo-600 border-indigo-300 hover:bg-indigo-50 gap-1"
              data-testid="revalidar-alertas-btn"
              title="Re-corre los monitores del caso sobre esta cuenta desde su creación hasta hoy y actualiza el conteo de movimientos"
              onClick={async () => {
                setRevalidando(true);
                try {
                  const res = await api.post(`/cases/${caso.id}/revalidar`);
                  const jobId = res.data?.job_id;
                  if (!jobId) throw new Error('No se pudo iniciar la revalidación');
                  let job = null;
                  for (let i = 0; i < 120; i++) {
                    await new Promise((r) => setTimeout(r, 2500));
                    const jr = await api.get(`/cases/revalidar-job/${jobId}`);
                    job = jr.data;
                    if (job?.estado === 'completado') break;
                  }
                  const r = job?.resultado || {};
                  if ((r.movimientos_nuevos_total || 0) > 0) {
                    toast.success(`Revalidación lista: +${r.movimientos_nuevos_total} movimiento(s) nuevo(s) detectado(s).`);
                  } else {
                    toast.info('Revalidación lista: no se encontraron movimientos nuevos.');
                  }
                  loadCaso();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error al revalidar alertas');
                } finally {
                  setRevalidando(false);
                }
              }}>
              <Zap size={12} />
              {revalidando ? 'Revalidando…' : 'Revalidar alertas'}
            </Button>

            <Button variant="outline" size="sm" disabled={verificandoWaya}
              className="h-8 text-xs text-emerald-700 border-emerald-300 hover:bg-emerald-50 gap-1"
              data-testid="verificar-waya-btn"
              title="Verifica en Waya que la cuenta de este caso YA NO tenga un CVU activo (auditoría de cierre/bloqueo)"
              onClick={async () => {
                setVerificandoWaya(true);
                try {
                  const res = await api.post('/waya/verificar-cierre-cvus', { case_ids: [caso.id] });
                  const jobId = res.data?.job_id;
                  if (!jobId) throw new Error('No se pudo iniciar la verificación');
                  let job = null;
                  for (let i = 0; i < 120; i++) {
                    await new Promise((r) => setTimeout(r, 2500));
                    const jr = await api.get(`/waya/verificar-cierre-job/${jobId}`);
                    job = jr.data;
                    if (job?.estado === 'completado') break;
                  }
                  const r = (job?.resultados || [])[0] || {};
                  if (r.resultado === 'CON_CVU') {
                    toast.warning(`⚠️ ${r.motivo}`);
                  } else if (r.resultado === 'SIN_CVU') {
                    toast.success('OK: la cuenta no tiene CVU activo en Waya.');
                  } else if (r.resultado === 'ACTIVA_INFO') {
                    toast.info(r.motivo || 'La cuenta sigue activa en Waya (cierre sin baja).');
                  } else {
                    toast.error(r.motivo || 'No se pudo verificar en Waya');
                  }
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Error al verificar en Waya');
                } finally {
                  setVerificandoWaya(false);
                }
              }}>
              <Shield size={12} />
              {verificandoWaya ? 'Verificando…' : 'Verificar en Waya'}
            </Button>

            {caso.waya_verificacion && (
              <Button variant="outline" size="sm" disabled={limpiandoWaya}
                className="h-8 text-xs text-slate-600 border-slate-300 hover:bg-slate-50 gap-1"
                data-testid="limpiar-verificacion-btn"
                title="Quita el estado de verificación de Waya de este caso (queda Sin verificar)"
                onClick={async () => {
                  setLimpiandoWaya(true);
                  try {
                    await api.post('/waya/limpiar-verificacion', { case_ids: [caso.id] });
                    toast.success('Verificación quitada. El caso quedó sin verificar.');
                    loadCaso();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Error al quitar la verificación');
                  } finally {
                    setLimpiandoWaya(false);
                  }
                }}>
                <X size={12} />
                {limpiandoWaya ? 'Quitando…' : 'Quitar verificación'}
              </Button>
            )}

            <Select value={caso.prioridad} onValueChange={handleChangePrioridad}>
              <SelectTrigger className="w-36" style={{ backgroundColor: '#fff' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORIDADES.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={caso.responsable_id || caso.asignado_a || 'sin_asignar'} onValueChange={(v) => v !== 'sin_asignar' && handleAssign(v)}>
              <SelectTrigger className="w-48" style={{ backgroundColor: '#fff' }}>
                <SelectValue>
                  {caso.responsable_nombre || caso.asignado_a || 'Sin asignar'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {users.map(u => (
                  <SelectItem key={u.id} value={u.id}>{u.nombre || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline"
              onClick={() => navigate(`/m/gestion-de-casos/network-analysis?caso=${caso.id}`)}
              className="bg-purple-600 text-white border-purple-600 hover:bg-purple-700 hover:border-purple-700"
            >
              <Share2 size={16} className="mr-2" />
              Ver Red
            </Button>
          </div>
        </div>
      </div>

      {/* Barra de Acciones Rápidas - Horizontal */}
      <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 via-indigo-50 to-blue-50 rounded-lg border border-purple-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-purple-800 mr-2">Acciones:</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowNoteDialog(true)}
            className="bg-white hover:bg-pink-50 text-slate-700 border-slate-200 hover:border-pink-300"
          >
            <StickyNote size={14} className="mr-1 text-pink-500" />
            Nota
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setUploadingFiles([]); setShowUploadDialog(true); }}
            className="bg-white hover:bg-orange-50 text-slate-700 border-slate-200 hover:border-orange-300"
          >
            <Upload size={14} className="mr-1 text-orange-500" />
            Documento
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={openSolicitudDialog}
            className="bg-white hover:bg-blue-50 text-slate-700 border-slate-200 hover:border-blue-300"
          >
            <ClipboardList size={14} className="mr-1 text-blue-500" />
            Solicitud
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setActiveTab('informes')}
            className="bg-white hover:bg-green-50 text-slate-700 border-slate-200 hover:border-green-300"
          >
            <FileOutput size={14} className="mr-1 text-green-500" />
            Informe
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/m/gestion-de-casos/network-analysis?caso=${caso.id}`)}
            className="bg-white hover:bg-purple-50 text-slate-700 border-slate-200 hover:border-purple-300"
          >
            <Share2 size={14} className="mr-1 text-purple-500" />
            Ver Red
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={async () => {
              try {
                toast.info('Generando PDF...');
                const response = await api.get(`/informes/caso/${caso.id}/pdf-completo`, {
                  responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `Caso_${caso.cuit || caso.titulo || 'informe'}_${new Date().toISOString().split('T')[0]}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success('PDF descargado');
              } catch (error) {
                toast.error('Error al generar PDF');
                console.error(error);
              }
            }}
            className="bg-white hover:bg-red-50 text-slate-700 border-slate-200 hover:border-red-300"
          >
            <FileText size={14} className="mr-1 text-red-500" />
            Exportar PDF
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEmailForm({ 
                email: '', 
                asunto: `Informe de Caso: ${caso.titulo || caso.cuit || 'Sin título'}`, 
                mensaje: `Adjunto el informe del caso ${caso.numero_referencia || caso.id}.\n\nSaludos.` 
              });
              setShowEmailDialog(true);
            }}
            className="bg-white hover:bg-blue-50 text-slate-700 border-slate-200 hover:border-blue-300"
          >
            <Mail size={14} className="mr-1 text-blue-500" />
            Enviar Email
          </Button>
          {/* Eliminar caso: va a la papelera (admins o permiso "eliminar") */}
          {puedeEliminarCaso && (
            <Button
              size="sm"
              variant="outline"
              data-testid="caso-eliminar-btn"
              onClick={async () => {
                if (!window.confirm('¿Enviar este caso a la papelera junto con sus alertas? Se puede recuperar durante 30 días.')) return;
                try {
                  await api.delete(`/cases/${caso.id}`);
                  toast.success('Caso enviado a la papelera');
                  navigate('/m/gestion-de-casos/casos');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error al eliminar el caso');
                }
              }}
              className="bg-white hover:bg-red-50 text-red-600 border-red-200 hover:border-red-300"
            >
              <Trash2 size={14} className="mr-1" />
              Eliminar
            </Button>
          )}
        </div>
      </div>

      {/* Contenido Principal - Ancho Completo */}
      <div className="space-y-6">
        <Card className="bg-white overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full justify-start bg-slate-100 rounded-none border-b p-0 overflow-x-auto flex-nowrap"
                style={{ scrollbarWidth: 'thin' }}
              >
                <TabsTrigger value="resumen" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <LayoutGrid size={16} />
                  Resumen
                </TabsTrigger>
                {(caso.alertas?.length > 0 || caso.alertas_ids?.length > 0 || caso.total_alertas > 0) && (
                <TabsTrigger value="alertas" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <AlertTriangle size={16} />
                  Alertas ({caso.total_alertas || caso.alertas?.length || caso.alertas_ids?.length || 0})
                </TabsTrigger>
                )}
                <TabsTrigger value="movimientos" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <Activity size={16} />
                  Movimientos ({caso.movimientos_ids?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="documentos" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <FileText size={16} />
                  Documentos ({caso.documentos?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="timeline" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <History size={16} />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="analisis" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <BarChart3 size={16} />
                  Análisis
                </TabsTrigger>
                {(caso.casos_anteriores?.length > 0 || caso.reincidencia > 0) && (
                  <TabsTrigger value="historial_cliente" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                    <History size={16} />
                    Historial ({(caso.casos_anteriores?.length || 0) + 1})
                  </TabsTrigger>
                )}
                {caso.casos_anteriores?.length > 0 && (
                  <TabsTrigger value="comparativa" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3" data-testid="tab-comparativa">
                    <GitMerge size={16} />
                    Comparativa
                  </TabsTrigger>
                )}
                {caso.es_unificado && caso.informe_vinculacion && (
                  <TabsTrigger value="vinculacion" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                    <GitMerge size={16} />
                    Vinculación
                  </TabsTrigger>
                )}
                <TabsTrigger value="ros" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <Shield size={16} />
                  Evaluar ROS
                </TabsTrigger>
                <TabsTrigger value="solicitudes" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <ClipboardList size={16} />
                  Solicitudes
                  {solicitudesVinculadas.length > 0 && (
                    <Badge variant="outline" className="ml-1 bg-blue-50 text-blue-700 text-xs">
                      {solicitudesVinculadas.length}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="informes" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <FileOutput size={16} />
                  Informes
                </TabsTrigger>
                <TabsTrigger value="investigar" className="rounded-none data-[state=active]:bg-white data-[state=active]:shadow-none flex items-center gap-2 px-4 py-3">
                  <Radar size={16} />
                  Investigar
                </TabsTrigger>
              </TabsList>

              {/* Tab Resumen */}
              <TabsContent value="resumen" className="p-6 space-y-4">
                <CasoResumen
                  caso={caso}
                  onOpenSearch={(type) => { setShowSearchDialog(type); setSearchTerm(''); setSearchResults([]); }}
                  onAddNote={() => setShowNoteDialog(true)}
                  onRemoveCuenta={handleRemoveCuenta}
                  onRemoveEmpresa={handleRemoveEmpresa}
                  navigate={navigate}
                />
              </TabsContent>

              {/* Tab Alertas */}
              <TabsContent value="alertas" className="p-6">
                {/* Si hay alertas embebidas (de monitores), mostrar el nuevo componente */}
                {caso.alertas && caso.alertas.length > 0 ? (
                  <AlertasEmbebidas 
                    caso={caso} 
                    onMovimientosFilter={(ids, tipo, cuentaId) => {
                      // Cambiar al tab de movimientos
                      const movTab = document.querySelector('[role="tab"][value="movimientos"]');
                      if (movTab) movTab.click();
                      
                      // Si hay IDs específicos, filtrar por ellos
                      if (ids && ids.length > 0) {
                        setMovimientosDetalle(prev => {
                          const filtered = prev.filter(m => ids.includes(m.id_waya || m.id));
                          return filtered.length > 0 ? filtered : prev;
                        });
                      } else if (cuentaId) {
                        // Si no hay IDs pero hay cuenta, filtrar por cuenta
                        setMovimientosDetalle(prev => {
                          const filtered = prev.filter(m => 
                            m.cvu_mov === cuentaId || 
                            m.cbu_cvu_destino === cuentaId ||
                            m.cuenta_id === cuentaId
                          );
                          return filtered.length > 0 ? filtered : prev;
                        });
                      }
                    }}
                    onRefresh={loadCaso}
                    onCasoUpdated={(casoActualizado) => {
                      setCaso(casoActualizado);
                    }}
                    onHasUnsavedChanges={(hasChanges) => {
                      // Manejar cambios no guardados
                      if (hasChanges) {
                        window.onbeforeunload = () => "Tienes cambios sin guardar. ¿Estás seguro de que deseas salir?";
                      } else {
                        window.onbeforeunload = null;
                      }
                    }}
                  />
                ) : (
                  /* Vista tradicional para alertas vinculadas por ID */
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant={vistaElementos === 'cards' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setVistaElementos('cards')}
                        >
                          <LayoutGrid size={14} />
                        </Button>
                        <Button
                          variant={vistaElementos === 'list' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setVistaElementos('list')}
                        >
                          <List size={14} />
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => { setShowSearchDialog('alerta'); setSearchTerm(''); setSearchResults([]); }}
                        style={{ backgroundColor: '#f59e0b' }}
                      >
                        <Plus size={14} className="mr-1" /> Agregar Alerta
                      </Button>
                    </div>

                    {loadingAlertas ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="animate-spin text-amber-500" size={24} />
                      </div>
                    ) : alertasDetalle.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <AlertTriangle size={40} className="mx-auto mb-2 opacity-50" />
                        <p>No hay alertas vinculadas a este caso</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={() => { setShowSearchDialog('alerta'); setSearchTerm(''); setSearchResults([]); }}
                        >
                          <Plus size={14} className="mr-1" /> Agregar primera alerta
                        </Button>
                      </div>
                    ) : vistaElementos === 'cards' ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {alertasDetalle.map(alerta => {
                          const isResolved = ['CERRADA', 'FALSO_POSITIVO', 'RESUELTA'].includes(alerta.estado);
                          const isEscalated = alerta.estado === 'ESCALADA';
                          const bgColor = isResolved ? 'bg-green-50 border-green-200' : 
                                          isEscalated ? 'bg-red-50 border-red-200' : 
                                          'bg-amber-50 border-amber-200';
                          const isExpanded = expandedAlertId === alerta.id;
                          return (
                            <div key={alerta.id} className={`rounded-lg border ${bgColor} hover:shadow-md transition-shadow relative overflow-hidden`}>
                          <div className="p-4">
                            {/* Badge de resolución */}
                            {isResolved && (
                              <div className="absolute top-2 right-2">
                                <CheckCircle size={20} className="text-green-600" />
                              </div>
                            )}
                            {isEscalated && (
                              <div className="absolute top-2 right-2">
                                <AlertTriangle size={20} className="text-red-600 animate-pulse" />
                              </div>
                            )}
                            
                            <div className="flex items-start justify-between mb-2">
                              <Badge className={`${
                                alerta.severidad === 'CRITICA' ? 'bg-red-100 text-red-700' :
                                alerta.severidad === 'ALTA' ? 'bg-orange-100 text-orange-700' :
                                alerta.severidad === 'MEDIA' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {alerta.severidad}
                              </Badge>
                              <Badge variant="outline" className={`text-xs ${
                                isResolved ? 'bg-green-100 text-green-700 border-green-300' :
                                isEscalated ? 'bg-red-100 text-red-700 border-red-300' :
                                ''
                              }`}>
                                {alerta.estado?.replace('_', ' ') || 'NUEVA'}
                              </Badge>
                            </div>
                            <h4 className={`font-medium text-sm text-slate-800 line-clamp-2 ${isResolved ? 'line-through opacity-60' : ''}`}>{alerta.titulo}</h4>
                            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{alerta.descripcion}</p>
                            
                            {/* Info adicional */}
                            <div className="flex items-center gap-3 mt-3 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                {new Date(alerta.fecha_creacion || alerta.created_at).toLocaleDateString()}
                              </span>
                              {alerta.movimientos_ids?.length > 0 && (
                                <button 
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                                  onClick={() => setExpandedAlertId(isExpanded ? null : alerta.id)}
                                >
                                  <Activity size={12} />
                                  {alerta.cantidad_movimientos || alerta.movimientos_ids.length} txns
                                  <ChevronDown size={12} className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              )}
                              {alerta.movimientos_nuevos > 0 && (
                                <span
                                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-semibold"
                                  data-testid={`alerta-nuevos-${alerta.id}`}
                                  title={`Antes: ${alerta.cantidad_movimientos_anterior ?? '-'} · Ahora: ${alerta.cantidad_movimientos}`}
                                >
                                  <Zap size={11} />
                                  +{alerta.movimientos_nuevos} nuevos desde la última validación
                                </span>
                              )}
                            </div>
                            
                            {/* Acciones de alerta */}
                            <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-200/50">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => navigate(`/m/monitoreo-transaccional/alerts/${alerta.id}`)}
                              >
                                <Eye size={12} className="mr-1" /> Ver Detalle
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 text-xs"
                                onClick={() => openAlertActionDialog(alerta)}
                              >
                                <Edit2 size={12} className="mr-1" /> {isResolved ? 'Reabrir' : 'Resolver'}
                              </Button>
                              <button
                                onClick={() => handleRemoveAlerta(alerta.id)}
                                className="ml-auto p-1 rounded hover:bg-red-100 text-red-500"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Panel expandible de movimientos */}
                          {isExpanded && alerta.movimientos_ids?.length > 0 && (
                            <div className="border-t border-slate-200 bg-white/80 p-3 max-h-96 overflow-y-auto">
                              <p className="text-xs font-medium text-slate-600 mb-2">Transacciones asociadas:</p>
                              <AlertMovimientosPreview alertId={alerta.id} movimientosIds={alerta.movimientos_ids} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {alertasDetalle.map(alerta => (
                      <div key={alerta.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100">
                        <div className="flex items-center gap-3 flex-1">
                          <AlertTriangle size={16} className="text-amber-500" />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{alerta.titulo}</p>
                            <p className="text-xs text-slate-500">{alerta.severidad} • {alerta.estado?.replace('_', ' ') || 'NUEVA'} • {new Date(alerta.fecha_creacion || alerta.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => navigate(`/m/monitoreo-transaccional/alerts/${alerta.id}`)}
                          >
                            <Eye size={12} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => openAlertActionDialog(alerta)}
                          >
                            <Edit2 size={12} />
                          </Button>
                          <button
                            onClick={() => handleRemoveAlerta(alerta.id)}
                            className="p-1 rounded hover:bg-red-100 text-red-500"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                  </>
                )}
              </TabsContent>

              {/* Tab Movimientos */}
              <TabsContent value="movimientos" className="p-6">
                <CasoMovimientosTab
                  movimientosDetalle={movimientosDetalle}
                  loadingMovimientos={loadingMovimientos}
                  vistaElementos={vistaElementos}
                  setVistaElementos={setVistaElementos}
                  onOpenSearch={() => { setShowSearchDialog('movimiento'); setSearchTerm(''); setSearchResults([]); }}
                  onRemoveMovimiento={handleRemoveMovimiento}
                />
              </TabsContent>

              {/* Tab Documentos */}
              <TabsContent value="documentos" className="p-6">
                <CasoDocumentosTab
                  caso={caso}
                  dragOver={dragOver}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onOpenUpload={() => { setUploadingFiles([]); setShowUploadDialog(true); }}
                  onPreviewDoc={setShowDocPreview}
                  onDeleteDoc={handleDeleteDocumento}
                />
              </TabsContent>

              {/* Tab Timeline */}
              <TabsContent value="timeline" className="p-6">
                <CasoTimeline caso={caso} />
              </TabsContent>

              {/* Tab Análisis Transaccional */}
              <TabsContent value="analisis" className="p-6">
                <TransactionCharts caseId={caso.id} hasCasosAnteriores={caso.casos_anteriores?.length > 0} />
              </TabsContent>

              {/* Tab Historial del Cliente */}
              <TabsContent value="historial_cliente" className="p-6">
                <ClienteHistorial caseId={caso.id} casoActual={caso} navigate={navigate} />
              </TabsContent>

              {/* Tab Comparativa side-by-side */}
              {caso.casos_anteriores?.length > 0 && (
                <TabsContent value="comparativa" className="p-0">
                  <CasoComparativaTab
                    caso={caso}
                    casosAnteriores={caso.casos_anteriores}
                    onIncorporated={() => loadCaso()}
                  />
                </TabsContent>
              )}

              {/* Tab Informe de Vinculación */}
              {caso.es_unificado && caso.informe_vinculacion && (
                <TabsContent value="vinculacion" className="p-6">
                  <InformeVinculacion caso={caso} navigate={navigate} />
                </TabsContent>
              )}

              {/* Tab Evaluación ROS */}
              <TabsContent value="ros" className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <Shield className="text-purple-600" />
                        Evaluación de Reporte de Operación Sospechosa
                      </h3>
                      <p className="text-sm text-slate-500">
                        Evalúe los factores de riesgo para determinar si este caso requiere generar un ROS
                      </p>
                    </div>
                  </div>
                  
                  <ROSEvaluationPanel 
                    casoId={caso.id} 
                    onEvaluationComplete={(decision) => {
                      toast.success(`Evaluación completada: ${decision}`);
                      loadCaso();
                    }}
                  />
                </div>
              </TabsContent>

              {/* Tab Solicitudes */}
              <TabsContent value="solicitudes" className="p-6">
                <CasoSolicitudes
                  caso={caso}
                  solicitudesVinculadas={solicitudesVinculadas}
                  onOpenVincular={() => setShowVincularDialog(true)}
                  onOpenNuevaSolicitud={openSolicitudDialog}
                  onDesvincular={desvincularSolicitud}
                  onRefresh={() => { loadSolicitudesVinculadas(); loadCaso(); }}
                  navigate={navigate}
                />
              </TabsContent>

              {/* Tab Informes */}
              <TabsContent value="informes" className="p-6">
                <InformesPanel caso={caso} onRefresh={loadCaso} />
              </TabsContent>

              {/* Tab Investigar */}
              <TabsContent value="investigar" className="p-6">
                <CasoInvestigar
                  caso={caso}
                  monitoresDisponibles={monitoresDisponibles}
                  monitoresSeleccionados={monitoresSeleccionados}
                  investigando={investigando}
                  resultadosInvestigacion={resultadosInvestigacion}
                  incluirWhitelist={incluirWhitelist}
                  agregandoAlerta={agregandoAlerta}
                  onToggleMonitor={toggleMonitorSeleccion}
                  onToggleTodos={toggleTodosMonitores}
                  onInvestigar={handleInvestigar}
                  onSetIncluirWhitelist={setIncluirWhitelist}
                  onAgregarAlerta={handleAgregarAlertaInvestigacion}
                />
              </TabsContent>
            </Tabs>
          </Card>

        {/* Sección Inferior: Comentarios, Links e Información en Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <CasoCommentsSection
            caso={caso}
            newComment={newComment}
            setNewComment={setNewComment}
            addingComment={addingComment}
            onAddComment={handleAddComment}
            onCaseUpdate={setCaso}
          />
          <CasoInfoPanel caso={caso} />
          <CaseLinksPanel caseId={caso.id} onNavigate={(id) => navigate(`/m/gestion-de-casos/caso/${id}`)} onCaseUpdate={setCaso} />
        </div>
      </div>

      {/* All dialogs extracted to CasoDetalleDialogs */}
      <CasoDetalleDialogs
        caso={caso} caseId={caseId} users={users}
        showCloseDialog={showCloseDialog} setShowCloseDialog={setShowCloseDialog}
        closeTemplates={closeTemplates} closeReason={closeReason} setCloseReason={setCloseReason}
        closeTipoResolucion={closeTipoResolucion} setCloseTipoResolucion={setCloseTipoResolucion}
        tiposResolucion={tiposResolucion}
        pendingCloseState={pendingCloseState} handleConfirmClose={handleConfirmClose}
        showUploadDialog={showUploadDialog} setShowUploadDialog={setShowUploadDialog}
        uploadingFiles={uploadingFiles} dragOver={dragOver}
        handleDragOver={handleDragOver} handleDragLeave={handleDragLeave}
        handleDrop={handleDrop} handleFileSelect={handleFileSelect}
        removeFileFromUpload={removeFileFromUpload} uploadDocuments={uploadDocuments}
        showNoteDialog={showNoteDialog} setShowNoteDialog={setShowNoteDialog}
        newNote={newNote} setNewNote={setNewNote} handleAddNote={handleAddNote}
        showSearchDialog={showSearchDialog} setShowSearchDialog={setShowSearchDialog}
        searchTerm={searchTerm} setSearchTerm={setSearchTerm}
        searchResults={searchResults} searching={searching}
        handleAddCuenta={handleAddCuenta} handleAddMovimiento={handleAddMovimiento}
        handleAddEmpresa={handleAddEmpresa} handleAddAlerta={handleAddAlerta}
        showDocPreview={showDocPreview} setShowDocPreview={setShowDocPreview}
        showAlertActionDialog={showAlertActionDialog} setShowAlertActionDialog={setShowAlertActionDialog}
        selectedAlertForAction={selectedAlertForAction} alertActionForm={alertActionForm}
        setAlertActionForm={setAlertActionForm} ALERT_STATES={ALERT_STATES}
        handleAlertAction={handleAlertAction}
        showVincularDialog={showVincularDialog} setShowVincularDialog={setShowVincularDialog}
        busquedaSolicitud={busquedaSolicitud} buscarSolicitudesParaVincular={buscarSolicitudesParaVincular}
        buscandoSolicitudes={buscandoSolicitudes} solicitudesBusqueda={solicitudesBusqueda}
        vincularSolicitud={vincularSolicitud}
        showSolicitudDialog={showSolicitudDialog} setShowSolicitudDialog={setShowSolicitudDialog}
        solicitudForm={solicitudForm} setSolicitudForm={setSolicitudForm}
        tiposSolicitud={tiposSolicitud} gruposSolicitud={gruposSolicitud}
        plantillasSolicitud={plantillasSolicitud} alertasDetalle={alertasDetalle}
        creandoSolicitud={creandoSolicitud}
        handleTipoSolicitudChange={handleTipoSolicitudChange}
        handlePlantillaSolicitudChange={handlePlantillaSolicitudChange}
        handleCrearSolicitud={handleCrearSolicitud}
        getCasoCvu={getCasoCvu} detectTipoBusqueda={detectTipoBusqueda}
        showEmailDialog={showEmailDialog} setShowEmailDialog={setShowEmailDialog}
        emailForm={emailForm} setEmailForm={setEmailForm}
        enviandoEmail={enviandoEmail} setEnviandoEmail={setEnviandoEmail}
      />

      {/* Modal enviar email con plantilla */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Send size={18} className="text-teal-600" /> Enviar Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {emailPreviewLoading && (
              <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="email-preview-loading">
                <RefreshCw size={12} className="animate-spin" /> Armando el email desde los datos del caso…
              </div>
            )}
            <div>
              <label className="text-xs text-slate-500 uppercase font-medium">Email destinatario</label>
              <div className="flex gap-2 mt-1">
                <input type="email" value={emailForm.email} onChange={e => setEmailForm({ ...emailForm, email: e.target.value })}
                  data-testid="email-destinatario-input"
                  placeholder="email@empresa.com" className="flex-1 h-9 text-sm rounded border border-slate-200 px-3" />
                <Button type="button" variant="outline" size="sm" onClick={buscarEmailWaya} disabled={buscandoWayaEmail}
                  data-testid="buscar-email-waya-btn" className="h-9 text-xs border-cyan-300 text-cyan-700 hover:bg-cyan-50 whitespace-nowrap">
                  {buscandoWayaEmail ? <RefreshCw size={12} className="animate-spin" /> : <><Eye size={12} className="mr-1" /> Buscar en Waya</>}
                </Button>
              </div>
              {!emailForm.email && !emailPreviewLoading && (
                <p className="text-[11px] text-amber-600 mt-1">No se encontró el email internamente. Ingresalo o buscalo en Waya.</p>
              )}
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase font-medium">Plantilla (opcional)</label>
              <select value={emailForm.plantilla_id} onChange={e => handleEmailPlantillaChange(e.target.value)}
                data-testid="email-plantilla-select"
                className="w-full h-9 text-sm rounded border border-slate-200 px-2 mt-1 bg-white">
                <option value="">Usar texto armado del caso…</option>
                {emailPlantillas.map(p => (
                  <option key={p.id} value={p.id}>{p.nombre}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase font-medium">Asunto</label>
              <input value={emailForm.asunto} onChange={e => setEmailForm({ ...emailForm, asunto: e.target.value })}
                data-testid="email-asunto-input"
                className="w-full h-9 text-sm rounded border border-slate-200 px-3 mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-500 uppercase font-medium">Contenido (editable)</label>
              <textarea value={emailForm.preview} onChange={e => setEmailForm({ ...emailForm, preview: e.target.value })}
                data-testid="email-contenido-textarea" rows={8}
                className="w-full text-xs rounded border border-slate-200 px-3 py-2 mt-1 whitespace-pre-wrap" />
            </div>
            <p className="text-[10px] text-slate-400">CCO automático: noresponder@waya.com.ar · El envío queda registrado en el timeline del caso.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmailModal(false)}>Cancelar</Button>
            <Button onClick={handleEnviarEmailModal} disabled={!emailForm.email || !emailForm.preview}
              data-testid="email-enviar-confirm-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white">
              <Send size={14} className="mr-1" /> Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Marcar configurada (manual) → En Vigilancia sin Waya */}
      <Dialog open={showConfigManualDialog} onOpenChange={setShowConfigManualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-cyan-600" />
              Marcar como configurada (manual)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              El caso pasará a <strong>En Vigilancia</strong> sin tocar Waya (la sucursal/comisión ya las
              configuraste a mano). Los datos son opcionales y quedan registrados. El nombre de la sucursal
              en Waya siempre es el mail de la cuenta.
            </p>
            <div>
              <Label className="text-sm">Mail de la cuenta (nombre de la sucursal)</Label>
              <Input
                value={configManualForm.email}
                onChange={e => setConfigManualForm(p => ({ ...p, email: e.target.value }))}
                placeholder="cuenta@ejemplo.com (opcional)"
                className="mt-1"
                data-testid="config-manual-email"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">N° Sucursal Waya</Label>
                <Input
                  value={configManualForm.sucursal_id}
                  onChange={e => setConfigManualForm(p => ({ ...p, sucursal_id: e.target.value }))}
                  placeholder="Ej. 2234 (opcional)"
                  className="mt-1"
                  data-testid="config-manual-sucursal"
                />
              </div>
              <div>
                <Label className="text-sm">Comisión (Rubro)</Label>
                <select
                  value={configManualForm.rubro_id}
                  onChange={e => setConfigManualForm(p => ({ ...p, rubro_id: e.target.value }))}
                  className="mt-1 w-full text-sm border border-slate-200 rounded-md px-2 py-2 bg-white"
                  data-testid="config-manual-rubro"
                >
                  <option value="">— Opcional —</option>
                  {configManualRubros.map(r => (
                    <option key={r.id} value={r.id}>{r.nombre}{r.porcentaje != null ? ` — ${r.porcentaje}%` : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfigManualDialog(false)}>Cancelar</Button>
            <Button onClick={handleConfigManualSubmit} disabled={guardandoConfigManual}
              className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="config-manual-confirm">
              {guardandoConfigManual ? 'Guardando...' : 'Marcar En Vigilancia'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showClasifDialog} onOpenChange={setShowClasifDialog}>
        <DialogContent className="max-w-lg" data-testid="clasificacion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 size={18} /> Clasificación del caso</DialogTitle>
          </DialogHeader>
          <div className="flex gap-2">
            <Button variant={clasifTipo === 'interno' ? 'default' : 'outline'} className={clasifTipo === 'interno' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => setClasifTipo('interno')} data-testid="clasif-tab-interno">Interno</Button>
            <Button variant={clasifTipo === 'externo' ? 'default' : 'outline'} className={clasifTipo === 'externo' ? 'bg-orange-600 hover:bg-orange-700' : ''} onClick={() => setClasifTipo('externo')} data-testid="clasif-tab-externo">Externo</Button>
            <Button variant={clasifTipo === 'auto' ? 'default' : 'outline'} onClick={() => setClasifTipo('auto')} data-testid="clasif-tab-auto">Automático</Button>
          </div>

          {clasifTipo === 'externo' && (
            <p className="text-sm text-slate-600 py-2">Se marcará el caso como <strong>EXTERNO</strong> manualmente (manda sobre el cálculo del padrón).</p>
          )}
          {clasifTipo === 'auto' && (
            <p className="text-sm text-slate-600 py-2">Se quita el override manual: la clasificación vuelve a calcularse según el padrón interno.</p>
          )}
          {clasifTipo === 'interno' && (
            <div className="space-y-3 py-1">
              <p className="text-sm text-slate-600">Marcá el caso como <strong>INTERNO</strong> y asigná la cuenta correspondiente.</p>
              <div className="flex gap-2">
                <Input placeholder="Buscar en padrón (CVU, CUIT o razón social)" value={padronQuery} onChange={(e) => setPadronQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && buscarPadron()} data-testid="padron-search-input" />
                <Button variant="outline" onClick={buscarPadron} disabled={padronLoading} data-testid="padron-search-btn">
                  {padronLoading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                </Button>
              </div>
              {padronResults.length > 0 && (
                <div className="max-h-44 overflow-y-auto border rounded-md divide-y" data-testid="padron-results">
                  {padronResults.map((r, i) => {
                    const cvu0 = (r.cvus && r.cvus[0] && (r.cvus[0].numero || r.cvus[0])) || '';
                    return (
                      <button key={i} className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm" data-testid={`padron-result-${i}`}
                        onClick={() => aplicarClasificacion({ tipo: 'interno', cuit: r.cuit, cvu: cvu0 })}>
                        <div className="font-medium text-slate-800">{r.razon_social || r.cuit}</div>
                        <div className="text-xs text-slate-500 font-mono">CUIT {r.cuit}{cvu0 ? ` · CVU ${cvu0}` : ''}</div>
                      </button>
                    );
                  })}
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400">o ingresá un CVU/CUIT manual:</span>
              </div>
              <div className="flex gap-2">
                <Input placeholder="CVU o CUIT" value={cuentaManual} onChange={(e) => setCuentaManual(e.target.value)} className="font-mono" data-testid="cuenta-manual-input" />
                <Button onClick={() => {
                  const v = cuentaManual.trim();
                  if (!v) { toast.error('Ingresá un CVU o CUIT'); return; }
                  const isCuit = v.replace(/[-\s]/g, '').length === 11;
                  aplicarClasificacion(isCuit ? { tipo: 'interno', cuit: v } : { tipo: 'interno', cvu: v });
                }} disabled={clasificando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cuenta-manual-apply">Asignar</Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClasifDialog(false)} disabled={clasificando}>Cancelar</Button>
            {clasifTipo !== 'interno' && (
              <Button onClick={() => aplicarClasificacion({ tipo: clasifTipo })} disabled={clasificando} data-testid="clasif-apply-btn">
                {clasificando ? 'Aplicando…' : 'Aplicar'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CasoDetalle;
